import { BsMetaUtil, CapType, LendingVaultExpanded } from "@bridgesplit/abf-react";
import { createRows, TableColumn, Table, Column, Text, Tooltip, Icon } from "@bridgesplit/ui";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { formatSeconds, getUnixTs, TIME } from "@bridgesplit/utils";
import { COPY } from "app/constants";

import { VaultCapsTableSummary } from "./type";

export default function VaultCapsTable({
    vaultExpanded,
    vaultCapsSummary
}: {
    vaultExpanded?: LendingVaultExpanded | undefined;
    vaultCapsSummary: VaultCapsTableSummary[];
}) {
    const columns = getVaultCapsTableColumns(vaultExpanded);

    return (
        <Table
            paginationProps={{
                pageSize: 5,
                type: "managed",
                defaultSortKey: "allocation"
            }}
            emptyPlaceholder="No active loans"
            loading={!vaultExpanded}
            rows={createRows(vaultCapsSummary, (s) => s.id)}
            columns={columns}
        />
    );
}

function getVaultCapsTableColumns(
    vaultExpanded: LendingVaultExpanded | undefined
): TableColumn<VaultCapsTableSummary>[] {
    const metadata = vaultExpanded?.principalMetadata;
    const columns: TableColumn<VaultCapsTableSummary>[] = [
        {
            dataIndex: "capType",
            title: "Cap Type",
            width: "20%",
            render: ({ data }) => <Text>{getCapTypeSource(data.capType)}</Text>
        },
        {
            width: "40%",
            dataIndex: "live1hrAmount",
            align: "right",
            title: "1-Hour Limits",
            render: ({ data }) => (
                <CapBreakdown
                    liveAmount={data.live1hrAmount}
                    liveAmountCompare={data.live24hrAmount}
                    maxAmount={data.max1hrAmount}
                    maxAmountCompare={data.max24hrAmount}
                    metadata={metadata}
                    refreshCountdown={getRefreshCountdown(data.last1hrPeriodStart, TIME.HOUR)}
                />
            )
        },
        {
            width: "40%",
            dataIndex: "live24hrAmount",
            title: "24-Hour Limits",
            align: "right",
            render: ({ data }) => (
                <CapBreakdown
                    liveAmount={data.live24hrAmount}
                    liveAmountCompare={data.live1hrAmount}
                    maxAmount={data.max24hrAmount}
                    maxAmountCompare={data.max1hrAmount}
                    metadata={metadata}
                    refreshCountdown={getRefreshCountdown(data.last24hrPeriodStart, TIME.DAY)}
                />
            )
        }
    ];

    return columns;
}

function CapBreakdown({
    liveAmount,
    maxAmount,
    liveAmountCompare,
    maxAmountCompare,
    metadata,
    refreshCountdown
}: {
    liveAmount: number | null;
    maxAmount: number | null;
    liveAmountCompare: number | null;
    maxAmountCompare?: number | null;
    metadata: TokenListMetadata | undefined;
    refreshCountdown?: number;
}) {
    if (liveAmount === null || maxAmount === null) {
        return (
            <Column sx={{ width: "100%", alignItems: "flex-end" }}>
                <Text color="disabled">{COPY.NO_LIMITS}</Text>
            </Column>
        );
    }

    const maxUtililzed = Math.min(liveAmount, liveAmountCompare ?? Number.MAX_VALUE);
    const maxAvailable = Math.min(maxAmount, maxAmountCompare ?? Number.MAX_VALUE);

    let tooltipTitle: string | undefined;
    if (refreshCountdown !== undefined) {
        if (refreshCountdown <= 0) {
            tooltipTitle = "Refreshing";
        } else {
            tooltipTitle = `Refreshes in ${formatSeconds(refreshCountdown)}`;
        }
    }

    return (
        <Tooltip title={tooltipTitle} placement="bottom">
            <Column sx={{ width: "100%", alignItems: "flex-end" }}>
                <Text>{BsMetaUtil.formatAmount(metadata, maxAmount)}</Text>
                <Text color="caption" variant="body2" svgColor="caption">
                    Available: {BsMetaUtil.formatAmount(metadata, Math.min(maxUtililzed, maxAvailable))}
                    {refreshCountdown !== undefined && <Icon type="tooltip" />}
                </Text>
            </Column>
        </Tooltip>
    );
}

function getCapTypeSource(capType: CapType): string {
    switch (capType) {
        case CapType.Supply:
            return "Deposits";
        case CapType.Borrow:
            return "Borrow";
        case CapType.Withdraw:
            return "Withdrawals";
    }
}

function getRefreshCountdown(lastPeriodStart: number | undefined, periodSeconds: number): number | undefined {
    if (!lastPeriodStart) return undefined;
    const now = getUnixTs();
    const elapsed = now - lastPeriodStart;
    const remaining = periodSeconds - elapsed;
    return remaining;
}
